import { SwipeableDrawer, Button } from "@mui/material";
import dayjs from "dayjs";
import { useTranslations } from "use-intl";

import { usePaymentContext } from "@/contexts/PaymentContext";
import { useLocaleInfo } from "@/hooks/useLocaleInfo";
import { formatCurrency } from "@/utils/currency";

interface InstallmentBreakdownDrawerProps {
  promoCodeDiscount?: number;
  totalPrice: number;
  open: boolean;
  onClose: () => void;
}

const InstallmentBreakdownDrawer = ({
  promoCodeDiscount,
  totalPrice,
  open,
  onClose,
}: InstallmentBreakdownDrawerProps) => {
  const t = useTranslations();
  const { locale } = useLocaleInfo();
  const { selectedInstallments } = usePaymentContext();

  const nextPaymentMonth = dayjs()
    .add(1, "month")
    .locale(locale)
    .format("MMMM");

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableRestoreFocus
    >
      <div className="pt-2">
        <div className="mb-6 text-center text-base font-medium">
          {t("instalmentBreakdown.title")}
        </div>
        <div className="mb-6 flex flex-col gap-3 text-sm">
          <div className="flex justify-between">
            <span>{t("instalmentBreakdown.numberOfInstalments")}</span>
            <span className="font-bold">
              {t("instalmentBreakdown.months", { count: selectedInstallments })}
            </span>
          </div>
          <div className="flex justify-between">
            <span>{t("instalmentBreakdown.nextPayment")}</span>
            <span className="font-bold">{nextPaymentMonth}</span>
          </div>
          {promoCodeDiscount ? (
            <div className="flex justify-between">
              <span>{t("promoCode.promoCodeDiscount")}</span>
              <div className="font-bold text-core-accent-500">
                {formatCurrency(promoCodeDiscount)}
              </div>
            </div>
          ) : null}
          <div className="flex justify-between">
            <span>{t("instalmentBreakdown.monthlyPayment")}</span>
            <span className="font-bold">
              {formatCurrency(totalPrice / selectedInstallments)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>{t("total")}</span>
            <span className="font-bold">{formatCurrency(totalPrice)}</span>
          </div>
        </div>

        <Button fullWidth variant="contained" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
    </SwipeableDrawer>
  );
};

export default InstallmentBreakdownDrawer;
